import { useCallback, useState } from 'react'

import { AthenaError } from '../lib/api/client.ts'
import { describeError, fieldOfError } from '../lib/api/messages.ts'
import { companies } from '../lib/api/resources.ts'
import type { CompanyResponse } from '../lib/api/types.ts'
import { COMPANY_UNINSTALL_WARNING } from '../lib/commands/uninstall.ts'
import { formatDayClock } from '../lib/format.ts'
import { Link, navigate } from '../lib/router.tsx'
import { useAction } from '../lib/useAction.ts'
import { useDebounced } from '../lib/useDebounced.ts'
import { useResource } from '../lib/useResource.ts'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { FleetIpDialog } from '../components/machines/FleetIp.tsx'
import { CompanyIsolationPolicyDialog } from '../components/machines/IsolationPolicy.tsx'
import { UninstallWarning } from '../components/machines/UninstallAgent.tsx'
import { RecordStateBadge } from '../components/primitives/Badge.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { ShortId } from '../components/primitives/CopyValue.tsx'
import { ConfirmDialog, FormDialog } from '../components/primitives/Dialog.tsx'
import { FilterToggle, SearchInput, TextField } from '../components/primitives/Field.tsx'
import { Menu, MenuItem, MenuSeparator } from '../components/primitives/Menu.tsx'
import { Pagination } from '../components/primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../components/primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../components/primitives/table.ts'
import { useToast } from '../components/primitives/Toast.tsx'

const PAGE_SIZE = 50

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; company: CompanyResponse }
  | { kind: 'toggle'; company: CompanyResponse }
  | { kind: 'delete'; company: CompanyResponse }
  | { kind: 'fleet-block'; company: CompanyResponse }
  | { kind: 'fleet-allow'; company: CompanyResponse }
  | { kind: 'isolation-policy'; company: CompanyResponse }
  | null

/**
 * Empresas.
 *
 * A empresa **é** o tenant: o `id` desta linha é o `tenant_id` de toda a
 * telemetria dela, e por isso ele é exibido e copiável — é o que se cola numa
 * consulta ao armazenamento de eventos.
 *
 * Ativar/desativar não está no formulário de edição, e sim numa ação própria
 * com confirmação. Desativar corta a ingestão da frota inteira do cliente na
 * próxima conexão de cada agente, mesmo com licença ativa; uma caixinha ao lado
 * do campo de razão social trataria isso como se fosse tipografia.
 */
export function Companies() {
  const toast = useToast()

  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounced(searchInput)
  const [includeInactive, setIncludeInactive] = useState(true)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [dialog, setDialog] = useState<Dialog>(null)

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      companies.list({ page, pageSize: PAGE_SIZE, search, includeInactive, includeDeleted }, signal),
    [page, search, includeInactive, includeDeleted],
  )

  const { data, error, loading, reload } = useResource(fetcher)

  const close = useCallback(() => setDialog(null), [])
  const refresh = useCallback(() => {
    setDialog(null)
    reload()
  }, [reload])

  return (
    <>
      <PageHeader
        title="Empresas"
        actions={
          <Button variant="primary" onClick={() => setDialog({ kind: 'create' })}>
            Nova empresa
          </Button>
        }
      />

      <Toolbar>
        <SearchInput
          label="Buscar empresa"
          placeholder="código ou razão social"
          value={searchInput}
          onValueChange={(value) => {
            setSearchInput(value)
            setPage(1)
          }}
          className="w-64"
        />
        <FilterToggle
          label="inativas"
          pressed={includeInactive}
          title="Incluir empresas desativadas na listagem"
          onPressedChange={(value) => {
            setIncludeInactive(value)
            setPage(1)
          }}
        />
        <FilterToggle
          label="removidas"
          pressed={includeDeleted}
          title="Incluir empresas removidas logicamente"
          onPressedChange={(value) => {
            setIncludeDeleted(value)
            setPage(1)
          }}
        />
        {loading && <span className="text-micro text-fg-muted">carregando…</span>}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error !== null ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data === null ? (
          <LoadingRow />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Nenhuma empresa encontrada."
            hint={search === '' ? undefined : `Nada corresponde a “${search}”.`}
            action={
              search === '' ? (
                <Button onClick={() => setDialog({ kind: 'create' })}>Nova empresa</Button>
              ) : undefined
            }
          />
        ) : (
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Código
                </th>
                <th scope="col" className={TH}>
                  Razão social
                </th>
                <th scope="col" className={TH}>
                  Situação
                </th>
                <th scope="col" className={TH}>
                  tenant_id
                </th>
                <th scope="col" className={TH}>
                  Criada
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((company) => (
                <tr key={company.id} className={TR}>
                  {/* O código exibido é o que VOLTOU do servidor, em maiúsculas.
                      Mostrar o que foi digitado faria a busca por ACME achar uma
                      linha escrita "acme", o que parece bug e não é. */}
                  <th scope="row" className={`${TD} font-mono font-normal whitespace-nowrap text-fg`}>
                    {company.companyCode}
                  </th>
                  <td className={`${TD} max-w-0`}>
                    <span className="block truncate" title={company.name}>
                      {company.name}
                    </span>
                  </td>
                  <td className={TD}>
                    <RecordStateBadge isActive={company.isActive} deletedAt={company.deletedAt} />
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <ShortId id={company.id} label="tenant_id" />
                  </td>
                  <td className={`${TD} tnum whitespace-nowrap`}>
                    {formatDayClock(company.createdAt)}
                  </td>
                  <td className={`${TD} text-right`}>
                    {company.deletedAt === null && (
                      <Menu trigger={<span>ações</span>} label={`Ações de ${company.companyCode}`}>
                        <MenuItem onSelect={() => setDialog({ kind: 'edit', company })}>
                          Editar razão social
                        </MenuItem>
                        <MenuItem onSelect={() => navigate(`/licencas?companyId=${company.id}`)}>
                          Ver licenças
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'fleet-block', company })}>
                          Bloquear IP na frota
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'fleet-allow', company })}>
                          Permitir IP/rede na frota
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'isolation-policy', company })}>
                          Regime de isolamento das máquinas novas
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem
                          danger={company.isActive}
                          onSelect={() => setDialog({ kind: 'toggle', company })}
                        >
                          {company.isActive ? 'Desativar' : 'Reativar'}
                        </MenuItem>
                        <MenuItem danger onSelect={() => setDialog({ kind: 'delete', company })}>
                          Remover
                        </MenuItem>
                      </Menu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data !== null && data.items.length > 0 && (
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          busy={loading}
          onPageChange={setPage}
        />
      )}

      {dialog?.kind === 'create' && <CreateDialog onClose={close} onCreated={refresh} />}

      {dialog?.kind === 'edit' && (
        <EditDialog company={dialog.company} onClose={close} onSaved={refresh} />
      )}

      {(dialog?.kind === 'fleet-block' || dialog?.kind === 'fleet-allow') && (
        <FleetIpDialog
          companyId={dialog.company.id}
          companyName={dialog.company.name}
          mode={dialog.kind === 'fleet-block' ? 'block' : 'allow'}
          onClose={close}
        />
      )}

      {/* Staff lê; só platform_admin troca (o diálogo esconde o seletor). */}
      {dialog?.kind === 'isolation-policy' && (
        <CompanyIsolationPolicyDialog company={dialog.company} onClose={close} />
      )}

      {dialog?.kind === 'toggle' && (
        <ToggleDialog company={dialog.company} onClose={close} onSaved={refresh} />
      )}

      {dialog?.kind === 'delete' && (
        <DeleteDialog
          company={dialog.company}
          onClose={close}
          onDeleted={() => {
            // A desinstalação sai do Athena; o 204 não diz quantas máquinas tinham agente ativo.
            toast.success(
              `Empresa ${dialog.company.companyCode} removida. As máquinas com agente ativo se desinstalam quando conectarem.`,
            )
            refresh()
          }}
        />
      )}
    </>
  )
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast()
  const action = useAction()
  const [companyCode, setCompanyCode] = useState('')
  const [name, setName] = useState('')

  const field = fieldOfError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Nova empresa"
      submitLabel="Criar empresa"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          const created = await companies.create({ companyCode, name })
          toast.success(`Empresa ${created.companyCode} criada.`)
          onCreated()
        })
      }
    >
      <TextField
        label="Código"
        value={companyCode}
        onValueChange={setCompanyCode}
        error={field === 'companyCode' ? describeError(action.error) : null}
        hint="Imutável depois de criada."
        maxLength={64}
        required
        mono
        autoFocus
      />
      <TextField
        label="Razão social"
        value={name}
        onValueChange={setName}
        error={field === 'name' ? describeError(action.error) : null}
        maxLength={200}
        required
      />
    </FormDialog>
  )
}

function EditDialog({
  company,
  onClose,
  onSaved,
}: {
  company: CompanyResponse
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [name, setName] = useState(company.name)

  const field = fieldOfError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`Editar ${company.companyCode}`}
      submitLabel="Salvar"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          // `isActive` vai com o valor atual, não omitido: o PUT substitui o
          // recurso inteiro, e deixar o campo de fora reativaria (ou desativaria)
          // a empresa como efeito colateral de renomear.
          await companies.update(company.id, { name, isActive: company.isActive })
          toast.success(`Empresa ${company.companyCode} atualizada.`)
          onSaved()
        })
      }
    >
      <TextField
        label="Razão social"
        value={name}
        onValueChange={setName}
        error={field === 'name' ? describeError(action.error) : null}
        maxLength={200}
        required
        autoFocus
      />
    </FormDialog>
  )
}

function ToggleDialog({
  company,
  onClose,
  onSaved,
}: {
  company: CompanyResponse
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const deactivating = company.isActive

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={deactivating ? `Desativar ${company.companyCode}?` : `Reativar ${company.companyCode}?`}
      consequence={
        deactivating
          ? 'A ingestão de toda a frota desta empresa para na próxima conexão de cada agente.'
          : 'Os agentes com licença ativa voltam a ser aceitos na próxima conexão.'
      }
      confirmLabel={deactivating ? 'Desativar' : 'Reativar'}
      destructive={deactivating}
      busy={action.busy}
      error={action.error !== null ? describeError(action.error) : null}
      onConfirm={() =>
        action.run(async () => {
          await companies.update(company.id, { name: company.name, isActive: !company.isActive })
          toast.success(
            deactivating
              ? `Empresa ${company.companyCode} desativada.`
              : `Empresa ${company.companyCode} reativada.`,
          )
          onSaved()
        })
      }
    />
  )
}

/** O que remover a empresa faz — inclusive com o agente das máquinas dela. */
export function DeleteCompanyConsequence() {
  return (
    <>
      A empresa some das listagens e a ingestão para.
      <UninstallWarning>{COMPANY_UNINSTALL_WARNING}</UninstallWarning>
    </>
  )
}

function DeleteDialog({
  company,
  onClose,
  onDeleted,
}: {
  company: CompanyResponse
  onClose: () => void
  onDeleted: () => void
}) {
  const action = useAction()

  // O 409 aqui tem um caminho de saída concreto, e é o único lugar da tela onde
  // um erro vira um link: a lista de licenças da empresa, que é exatamente o
  // que precisa ser revogado antes de tentar de novo.
  const blocked =
    action.error instanceof AthenaError && action.error.code === 'company_has_live_product_keys'

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`Remover ${company.companyCode}?`}
      consequence={<DeleteCompanyConsequence />}
      confirmLabel="Remover"
      busy={action.busy}
      error={
        blocked ? (
          <span>
            {describeError(action.error)}{' '}
            <Link href={`/licencas?companyId=${company.id}`} className="underline">
              Ver licenças desta empresa
            </Link>
          </span>
        ) : action.error !== null ? (
          describeError(action.error)
        ) : null
      }
      onConfirm={() =>
        action.run(async () => {
          await companies.remove(company.id)
          onDeleted()
        })
      }
    />
  )
}
